import React, { useState, useEffect } from 'react';

const SESSION_KEY = 'string_admin_auth';

function getStoredCreds() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || 'Invalid credentials.');
      }
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ email, password }));
      onLogin({ email, password });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F5F0EB' }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '2.5rem 2rem', width: '100%', maxWidth: '380px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <h1 style={{ fontSize: '1.4rem', color: '#5D4037', margin: '0 0 0.25rem' }}>String Admin</h1>
        <p style={{ color: '#aaa', fontSize: '0.85rem', margin: '0 0 1.75rem' }}>Owner dashboard — restricted access</p>
        <form onSubmit={submit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontSize: '0.8rem', color: '#888', display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
              style={{ width: '100%', padding: '0.6rem 0.85rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.95rem', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ fontSize: '0.8rem', color: '#888', display: 'block', marginBottom: '0.3rem', fontWeight: '500' }}>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
              style={{ width: '100%', padding: '0.6rem 0.85rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.95rem', boxSizing: 'border-box' }} />
          </div>
          {error && <div style={{ background: '#fdecea', color: '#c62828', fontSize: '0.85rem', padding: '0.5rem 0.85rem', borderRadius: '6px', marginBottom: '1rem' }}>{error}</div>}
          <button type="submit" disabled={loading}
            style={{ width: '100%', background: '#5D4037', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.75rem', fontSize: '1rem', fontWeight: '600', cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem 1.75rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', flex: 1, minWidth: '160px', borderTop: accent ? `3px solid ${accent}` : '3px solid transparent' }}>
      <div style={{ fontSize: '2rem', fontWeight: '700', color: accent || '#5D4037', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.4rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      {sub && <div style={{ fontSize: '0.78rem', color: '#bbb', marginTop: '0.2rem' }}>{sub}</div>}
    </div>
  );
}

function pct(num, denom) {
  if (!denom || denom === 0) return '—';
  return `${Math.round((num / denom) * 100)}%`;
}

function MacroDashboard({ macro }) {
  if (!macro) return null;
  const { sent_30_days, total_failed, missed_windows, emails_opened, emails_tracked, sms_delivered, sms_tracked, recent_failures } = macro;

  const failedRows = recent_failures || [];

  const failureSub = [
    total_failed > 0 ? `${total_failed} total` : null,
    missed_windows > 0 ? `${missed_windows} missed window${missed_windows !== 1 ? 's' : ''}` : null,
  ].filter(Boolean).join(' · ') || 'SMS undelivered + email bounced + missed windows';

  return (
    <div style={{ marginBottom: '2rem' }}>
      <h2 style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Last 30 Days</h2>
      <div style={{ display: 'flex', gap: '0.875rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <StatCard label="Sent" value={sent_30_days} sub="reminders + holidays" />
        <StatCard label="Delivery failures" value={total_failed} sub={failureSub} accent={total_failed > 0 ? '#C62828' : undefined} />
        <StatCard label="SMS delivery rate" value={pct(sms_delivered, sms_tracked)} sub={sms_tracked > 0 ? `${sms_delivered} of ${sms_tracked} tracked` : 'no tracked messages yet'} accent={sms_tracked > 0 ? '#2E7D32' : undefined} />
        <StatCard label="Email open rate" value={pct(emails_opened, emails_tracked)} sub={emails_tracked > 0 ? `${emails_opened} of ${emails_tracked} tracked` : 'no tracked messages yet'} accent={emails_tracked > 0 ? '#1565C0' : undefined} />
      </div>

      {failedRows.length > 0 && (
        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: '0.5rem' }}>
          <div style={{ padding: '0.85rem 1.25rem', borderBottom: '1px solid #f0ebe6', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#C62828' }}>⚠ Recent Delivery Failures</span>
            <span style={{ fontSize: '0.75rem', color: '#aaa' }}>— top {failedRows.length} most recent</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f0ebe6', background: '#faf8f5' }}>
                {['When', 'User', 'For', 'Channel', 'Status'].map(h => (
                  <th key={h} style={{ padding: '0.45rem 1rem', textAlign: 'left', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {failedRows.map((f, i) => {
                const subject = f.source === 'holiday'
                  ? (f.holiday_name || 'Holiday')
                  : `${f.contact_first || ''}${f.contact_last ? ' ' + f.contact_last : ''}`.trim() || 'Contact';
                const hasSms = f.channels && f.channels.includes('sms');
                const hasEmail = f.channels && f.channels.includes('email');
                const smsFailed = f.sms_status && ['failed', 'undelivered'].includes(f.sms_status);
                const emailFailed = f.email_status === 'bounced';
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f8f4f0', background: i % 2 === 0 ? '#fff' : '#fdfbf9' }}>
                    <td style={{ padding: '0.6rem 1rem', color: '#888', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{formatDateTime(f.sent_at)}</td>
                    <td style={{ padding: '0.6rem 1rem' }}>
                      <div style={{ fontWeight: '600', color: '#C62828' }}>{f.user_name || '—'}</div>
                      <div style={{ fontSize: '0.72rem', color: '#bbb' }}>{f.user_phone || f.user_email || ''}</div>
                    </td>
                    <td style={{ padding: '0.6rem 1rem', color: '#555' }}>{subject}</td>
                    <td style={{ padding: '0.6rem 1rem' }}>
                      {f.missed_window ? (
                        <span style={{ color: '#aaa', fontSize: '0.75rem' }}>—</span>
                      ) : (
                        <span style={{ display: 'inline-flex', gap: '4px' }}>
                          {hasSms && <span style={{ background: '#E8F5E9', color: '#2E7D32', fontSize: '0.7rem', padding: '2px 6px', borderRadius: '8px' }}>💬 Text</span>}
                          {hasEmail && <span style={{ background: '#E3F2FD', color: '#1565C0', fontSize: '0.7rem', padding: '2px 6px', borderRadius: '8px' }}>📧 Email</span>}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0.6rem 1rem' }}>
                      {f.missed_window ? (
                        <span style={{ background: '#FFF3E0', color: '#E65100', fontSize: '0.7rem', padding: '2px 8px', borderRadius: '8px', fontWeight: '600' }}>⚠ Window missed</span>
                      ) : (
                        <span style={{ display: 'inline-flex', gap: '4px', flexWrap: 'wrap' }}>
                          {hasSms && smsFailed && <span style={{ background: '#FFEBEE', color: '#C62828', fontSize: '0.7rem', padding: '2px 6px', borderRadius: '8px', fontWeight: '600' }}>✗ Text {f.sms_status}</span>}
                          {hasEmail && emailFailed && <span style={{ background: '#FFEBEE', color: '#C62828', fontSize: '0.7rem', padding: '2px 6px', borderRadius: '8px', fontWeight: '600' }}>✗ Email bounced</span>}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {failedRows.length === 0 && (
        <div style={{ background: '#F1F8E9', borderRadius: '10px', padding: '0.75rem 1.25rem', fontSize: '0.83rem', color: '#558B2F', fontWeight: '500' }}>
          ✓ No delivery failures on record
        </div>
      )}
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function prefBadge(pref) {
  const map = { sms: ['💬', '#E8F5E9', '#2E7D32'], email: ['📧', '#E3F2FD', '#1565C0'], both: ['💬📧', '#FFF3E0', '#E65100'] };
  const [icon, bg, color] = map[pref] || map.both;
  return <span style={{ background: bg, color, fontSize: '0.75rem', padding: '2px 8px', borderRadius: '12px', fontWeight: '500' }}>{icon} {pref}</span>;
}

function channelBadge(channels) {
  if (!channels) return <span style={{ fontSize: '0.75rem', color: '#bbb', fontStyle: 'italic' }}>unknown</span>;
  const hasSms = channels.includes('sms');
  const hasEmail = channels.includes('email');
  return (
    <span style={{ display: 'inline-flex', gap: '4px' }}>
      {hasSms && <span style={{ background: '#E8F5E9', color: '#2E7D32', fontSize: '0.72rem', padding: '2px 7px', borderRadius: '10px', fontWeight: '500' }}>💬 Text</span>}
      {hasEmail && <span style={{ background: '#E3F2FD', color: '#1565C0', fontSize: '0.72rem', padding: '2px 7px', borderRadius: '10px', fontWeight: '500' }}>📧 Email</span>}
    </span>
  );
}

function smsStatusBadge(status) {
  if (!status) return <span style={{ fontSize: '0.7rem', color: '#bbb' }}>pending</span>;
  const map = {
    delivered: ['✓ Delivered', '#E8F5E9', '#2E7D32'],
    sent:      ['✓ Sent',      '#E8F5E9', '#2E7D32'],
    sending:   ['Sending…',   '#FFF8E1', '#F57F17'],
    queued:    ['Queued',     '#FFF8E1', '#F57F17'],
    undelivered: ['✗ Undelivered', '#FFEBEE', '#C62828'],
    failed:    ['✗ Failed',   '#FFEBEE', '#C62828'],
  };
  const [label, bg, color] = map[status] || [status, '#f5f5f5', '#555'];
  return <span style={{ fontSize: '0.7rem', background: bg, color, padding: '2px 6px', borderRadius: '8px', fontWeight: '500' }}>{label}</span>;
}

function emailStatusBadge(status, opened, openedAt) {
  const badges = [];
  if (status) {
    const map = {
      delivered:  ['✓ Delivered', '#E8F5E9', '#2E7D32'],
      sent:       ['✓ Sent',      '#E8F5E9', '#2E7D32'],
      opened:     ['✓ Delivered', '#E8F5E9', '#2E7D32'],
      bounced:    ['✗ Bounced',   '#FFEBEE', '#C62828'],
      complained: ['⚠ Complained', '#FFF3E0', '#E65100'],
    };
    const [label, bg, color] = map[status] || [status, '#f5f5f5', '#555'];
    badges.push(<span key="status" style={{ fontSize: '0.7rem', background: bg, color, padding: '2px 6px', borderRadius: '8px', fontWeight: '500' }}>{label}</span>);
  } else {
    badges.push(<span key="pending" style={{ fontSize: '0.7rem', color: '#bbb' }}>pending</span>);
  }
  if (opened) {
    const when = openedAt ? ` ${formatDateTime(openedAt)}` : '';
    badges.push(<span key="opened" style={{ fontSize: '0.7rem', background: '#EDE7F6', color: '#5E35B1', padding: '2px 6px', borderRadius: '8px', fontWeight: '500', marginLeft: '3px' }} title={`Opened${when}`}>👁 Opened</span>);
  }
  return <span style={{ display: 'inline-flex', gap: '3px', alignItems: 'center' }}>{badges}</span>;
}

function reminderLabel(type) {
  const map = { '4week': '4 weeks out', '2week': '2 weeks out', '1week': '1 week out', 'dayof': 'Day-of' };
  return map[type] || type;
}

function MessageLog({ userId, creds }) {
  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`/api/admin/users/${userId}/messages`, {
      headers: { 'x-admin-email': creds.email, 'x-admin-password': creds.password }
    })
      .then(r => { if (!r.ok) throw new Error('Failed to load'); return r.json(); })
      .then(d => setMessages(d.messages))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [userId, creds]);

  if (loading) return <div style={{ padding: '1rem 1.5rem', color: '#aaa', fontSize: '0.85rem' }}>Loading message history...</div>;
  if (error) return <div style={{ padding: '1rem 1.5rem', color: '#c62828', fontSize: '0.85rem' }}>Error: {error}</div>;
  if (!messages || messages.length === 0) return (
    <div style={{ padding: '1rem 1.5rem', color: '#bbb', fontSize: '0.85rem', fontStyle: 'italic' }}>No messages sent yet.</div>
  );

  return (
    <div style={{ padding: '0.75rem 1rem 1rem', background: '#faf8f5' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #e8e0d8' }}>
            <th style={{ padding: '0.4rem 0.75rem', textAlign: 'left', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Sent</th>
            <th style={{ padding: '0.4rem 0.75rem', textAlign: 'left', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>For</th>
            <th style={{ padding: '0.4rem 0.75rem', textAlign: 'left', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>Timing</th>
            <th style={{ padding: '0.4rem 0.75rem', textAlign: 'left', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>Text delivery</th>
            <th style={{ padding: '0.4rem 0.75rem', textAlign: 'left', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}>Email delivery</th>
          </tr>
        </thead>
        <tbody>
          {messages.map((m, i) => {
            const hasSms = m.channels && m.channels.includes('sms');
            const hasEmail = m.channels && m.channels.includes('email');
            return (
              <tr key={m.id} style={{ borderBottom: '1px solid #f0ebe6', background: i % 2 === 0 ? 'transparent' : '#f5f0eb22' }}>
                <td style={{ padding: '0.55rem 0.75rem', color: '#666', whiteSpace: 'nowrap' }}>{formatDateTime(m.sent_at)}</td>
                <td style={{ padding: '0.55rem 0.75rem' }}>
                  <div style={{ fontWeight: '500', color: '#2c2c2c' }}>{m.subject}</div>
                  {m.event_type && m.event_type !== 'holiday' && (
                    <div style={{ fontSize: '0.72rem', color: '#bbb', textTransform: 'capitalize' }}>{m.event_type}</div>
                  )}
                </td>
                <td style={{ padding: '0.55rem 0.75rem', color: '#666', whiteSpace: 'nowrap' }}>{reminderLabel(m.reminder_type)}</td>
                <td style={{ padding: '0.55rem 0.75rem' }}>
                  {hasSms ? smsStatusBadge(m.sms_status) : <span style={{ color: '#e0e0e0', fontSize: '0.7rem' }}>—</span>}
                </td>
                <td style={{ padding: '0.55rem 0.75rem' }}>
                  {hasEmail ? emailStatusBadge(m.email_status, m.email_opened, m.email_opened_at) : <span style={{ color: '#e0e0e0', fontSize: '0.7rem' }}>—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Dashboard({ creds, onLogout }) {
  const [data, setData] = useState(null);
  const [macro, setMacro] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortCol, setSortCol] = useState('id');
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');
  const [expandedUserId, setExpandedUserId] = useState(null);

  useEffect(() => {
    const headers = { 'x-admin-email': creds.email, 'x-admin-password': creds.password };
    Promise.all([
      fetch('/api/admin/dashboard', { headers })
        .then(r => { if (!r.ok) throw new Error('Access denied'); return r.json(); }),
      fetch('/api/admin/macro', { headers })
        .then(r => r.ok ? r.json() : null)
        .catch(() => null)
    ])
      .then(([dash, mac]) => { setData(dash); setMacro(mac); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [creds]);

  if (loading) return <div style={{ padding: '2rem', color: '#888' }}>Loading...</div>;
  if (error) return <div style={{ padding: '2rem', color: '#c62828' }}>Error: {error}</div>;
  if (!data) return null;

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const filtered = data.users.filter(u => {
    const q = search.toLowerCase();
    return !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').includes(q) || (u.phone || '').includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortCol] ?? '';
    let bv = b[sortCol] ?? '';
    if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  const TH = ({ col, children }) => (
    <th onClick={() => toggleSort(col)} style={{ padding: '0.65rem 0.85rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', borderBottom: '2px solid #f0ebe6', background: '#faf8f5' }}>
      {children}{sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : <span style={{ color: '#ddd' }}> ↕</span>}
    </th>
  );

  const toggleExpand = (userId) => {
    setExpandedUserId(prev => prev === userId ? null : userId);
  };

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem 1.25rem', fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem' }}>
        <div>
          <h1 style={{ margin: 0, color: '#5D4037', fontSize: '1.5rem' }}>Owner Dashboard</h1>
          <p style={{ color: '#aaa', fontSize: '0.85rem', margin: '0.25rem 0 0' }}>Internal metrics — not visible to users</p>
        </div>
        <button onClick={onLogout} style={{ background: 'transparent', color: '#aaa', border: '1px solid #ddd', borderRadius: '6px', padding: '0.4rem 0.85rem', fontSize: '0.85rem', cursor: 'pointer' }}>Sign out</button>
      </div>

      <MacroDashboard macro={macro} />

      <hr style={{ border: 'none', borderTop: '2px solid #f0ebe6', marginBottom: '1.75rem' }} />

      <h2 style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#aaa', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em' }}>All Time</h2>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2.5rem' }}>
        <StatCard label="Total Users" value={data.totals.users} />
        <StatCard label="Total Contacts" value={data.totals.contacts} sub={`avg ${data.totals.users ? (data.totals.contacts / data.totals.users).toFixed(1) : 0} per user`} />
        <StatCard label="Messages Sent" value={data.totals.messages_sent} sub="reminders + holiday" />
      </div>

      <hr style={{ border: 'none', borderTop: '2px solid #f0ebe6', marginBottom: '2rem' }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#2c2c2c' }}>
          Users <span style={{ fontSize: '0.85rem', color: '#aaa', fontWeight: '400' }}>({filtered.length})</span>
        </h2>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or phone..."
          style={{ padding: '0.45rem 0.85rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.875rem', width: '260px' }} />
      </div>

      <div style={{ overflowX: 'auto', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: '0.875rem' }}>
          <thead>
            <tr>
              <TH col="name">Name</TH>
              <TH col="phone">Phone</TH>
              <TH col="notify_pref">Via</TH>
              <TH col="plan">Plan</TH>
              <TH col="contact_count">Contacts</TH>
              <TH col="messages_sent">Messages</TH>
              <TH col="next_message_date">Next Message</TH>
              <TH col="created_at">Joined</TH>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#bbb' }}>No users found.</td></tr>
            )}
            {sorted.map((u, i) => {
              const isExpanded = expandedUserId === u.id;
              return (
                <React.Fragment key={u.id}>
                  <tr style={{ borderBottom: isExpanded ? 'none' : '1px solid #f5f0eb', background: isExpanded ? '#faf6f2' : (i % 2 === 0 ? '#fff' : '#fdfbf9') }}>
                    <td style={{ padding: '0.75rem 0.85rem' }}>
                      <div style={{ fontWeight: '500', color: '#2c2c2c' }}>{u.name || '—'}</div>
                      {u.email && <div style={{ fontSize: '0.78rem', color: '#999' }}>{u.email}</div>}
                    </td>
                    <td style={{ padding: '0.75rem 0.85rem', color: '#555', whiteSpace: 'nowrap' }}>{u.phone}</td>
                    <td style={{ padding: '0.75rem 0.85rem' }}>{prefBadge(u.notify_pref)}</td>
                    <td style={{ padding: '0.75rem 0.85rem' }}>
                      {u.plan === 'premium'
                        ? <span style={{ background: '#5D4037', color: '#fff', fontSize: '0.72rem', fontWeight: '700', padding: '2px 8px', borderRadius: '10px', letterSpacing: '0.04em' }}>PREMIUM</span>
                        : <span style={{ background: '#f0ebe6', color: '#aaa', fontSize: '0.72rem', fontWeight: '600', padding: '2px 8px', borderRadius: '10px' }}>Free</span>
                      }
                    </td>
                    <td style={{ padding: '0.75rem 0.85rem', textAlign: 'center', fontWeight: '600', color: '#5D4037' }}>{u.contact_count}</td>
                    <td style={{ padding: '0.75rem 0.85rem', textAlign: 'center' }}>
                      <button
                        onClick={() => toggleExpand(u.id)}
                        style={{
                          background: isExpanded ? '#5D4037' : '#f0ebe6',
                          color: isExpanded ? '#fff' : '#5D4037',
                          border: 'none', borderRadius: '6px',
                          padding: '0.25rem 0.65rem', fontSize: '0.82rem',
                          fontWeight: '600', cursor: 'pointer',
                          minWidth: '36px'
                        }}
                        title="View message history"
                      >
                        {u.messages_sent} {isExpanded ? '▲' : '▼'}
                      </button>
                    </td>
                    <td style={{ padding: '0.75rem 0.85rem' }}>
                      {u.next_message_date ? (
                        <div>
                          <div style={{ color: '#2c2c2c', fontWeight: '500' }}>{formatDate(u.next_message_date)}</div>
                          {u.next_message_event && <div style={{ fontSize: '0.75rem', color: '#aaa' }}>{u.next_message_event}</div>}
                        </div>
                      ) : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.75rem 0.85rem', color: '#aaa', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {u.created_at ? formatDate(u.created_at.slice(0, 10)) : '—'}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr style={{ borderBottom: '2px solid #e8e0d8' }}>
                      <td colSpan="8" style={{ padding: 0 }}>
                        <MessageLog userId={u.id} creds={creds} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const [creds, setCreds] = useState(getStoredCreds);

  const handleLogin = (c) => setCreds(c);
  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setCreds(null);
  };

  if (!creds) return <LoginForm onLogin={handleLogin} />;
  return <Dashboard creds={creds} onLogout={handleLogout} />;
}
